import { moodColor, moodLabel, signedFigure } from '@adhd/shared';
import { formatTime } from '../lib/mood.js';

/** The day's most recent check-in, or null for a day with none. */
export interface LastCheckin {
  mood: number;
  recordedAt: string;
  /** Known on Today, where the fuller form adds to this check-in (14 Oct 2026). */
  id?: string;
}

/**
 * The one row at the top of Today, closed.
 *
 * ITS OWN FILE, so a test can render it. The same reason `dayHeading` has one:
 * CheckIn.tsx imports react-router and the API client, so nothing could render
 * this without standing up most of the app — and what it says is the part most
 * able to go quietly false.
 *
 * WHAT IT MAY NOT SAY. Before 2 October the check-in form itself sat here all
 * day, and after a save `reset()` emptied it while the component stayed
 * mounted, so a day with three check-ins opened with an untouched slider
 * reading "Not said yet". Nick: "a slider in my face 24/7 with a text box."
 *
 * The obvious replacement — keep the card, disable the slider, park the thumb
 * on the last value — fails twice over, and both are drawn as corrections in
 * docs/today-shape-2026-10-02/mocks/today-next.html:
 *
 *   1. It would keep the heading "Overall right now" above a number given at
 *      9am. At 11pm that is false, and it would be the most prominent
 *      true-looking string on the screen. So this row is PAST TENSE and
 *      carries its own clock time, and the words "right now" appear nowhere in
 *      it.
 *
 *   2. A parked thumb is a loaded gun: `onPointerUp` commits
 *      `e.currentTarget.value`, so a thumb left at -3 records -3 from a brush
 *      of the track. There is no slider in this row at all.
 *
 * NO ATTRIBUTION. It says "Rough -3", never "you said Rough -3". A check-in
 * can also be written from the mood offer on a journal entry, so a speaker is
 * a claim this row cannot make about every row it draws. A figure and a clock
 * are facts about the record and need nobody's name on them.
 */
export function CheckinRow({
  last,
  onOpen,
}: {
  last: LastCheckin | null;
  onOpen: () => void;
}): JSX.Element {
  return (
    /*
     * A DISCLOSURE, and it has to say so.
     *
     * This row unmounts itself and renders the form in its place. With a
     * keyboard that left `document.activeElement` on `<body>`: the button that
     * had focus disappeared and nothing took its place, so the one action the
     * app exists for gave no outline, no announcement and no sign it had
     * worked. `aria-expanded` names the relationship, and the form moves focus
     * to the slider when it opens -- see `openRef` in CheckIn.tsx.
     *
     * `aria-label` carries what the visible text leaves to position: a word and
     * a clock on their own do not say they are a check-in, or that tapping
     * starts another.
     */
    <button
      className="option option-row checkin-row"
      onClick={onOpen}
      aria-expanded={false}
      aria-label={
        last === null
          ? 'Check in. How are you doing? About 15 seconds.'
          : `Check in again. Last check-in ${moodLabel(last.mood)} ${signedFigure(last.mood)} at ${formatTime(last.recordedAt)}.`
      }
    >
      {last === null ? (
        <>
          <strong className="grow">How are you doing?</strong>
          <span className="faint">15 sec</span>
        </>
      ) : (
        <>
          <strong className="grow" style={{ color: moodColor(last.mood) }}>
            {moodLabel(last.mood)} {signedFigure(last.mood)}
          </strong>
          <span className="faint">{formatTime(last.recordedAt)}</span>
        </>
      )}
    </button>
  );
}
